import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@medikei/ui';
import { Mail, Pill, CreditCard, Video, Brain, RefreshCw, CheckCircle, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { httpClient } from '@/api';

interface ServiceStatus {
  name: string;
  healthy: boolean;
  latencyMs?: number;
  message?: string;
  provider?: string;
}

interface HealthResponse {
  services: ServiceStatus[];
  timestamp: string;
}

interface EmailTestResponse {
  success: boolean;
  messageId?: string;
  message: string;
  latencyMs?: number;
}

interface TestResponse {
  healthy: boolean;
  message: string;
  latencyMs?: number;
  circuitState?: string;
  apiVersion?: string;
}

interface AiTestResponse {
  success: boolean;
  content?: string;
  model?: string;
  latencyMs?: number;
  message: string;
}

const serviceConfig = [
  {
    name: 'email',
    label: 'Email',
    description: 'SMTP / Resend',
    icon: Mail,
    color: 'bg-blue-100 text-blue-600',
    testEndpoint: 'test/email',
    needsInput: true,
    inputLabel: 'Email destination',
    inputPlaceholder: 'admin@medikei.com.br',
    inputType: 'email',
  },
  {
    name: 'memed',
    label: 'Memed',
    description: 'Prescrição Digital',
    icon: Pill,
    color: 'bg-purple-100 text-purple-600',
    testEndpoint: 'test/memed',
    needsInput: false,
  },
  {
    name: 'pagbank',
    label: 'PagBank',
    description: 'Pagamentos',
    icon: CreditCard,
    color: 'bg-green-100 text-green-600',
    testEndpoint: 'test/pagbank',
    needsInput: false,
  },
  {
    name: 'daily',
    label: 'Daily.co',
    description: 'Videochamadas',
    icon: Video,
    color: 'bg-orange-100 text-orange-600',
    testEndpoint: 'test/daily',
    needsInput: false,
  },
  {
    name: 'ai',
    label: 'AI',
    description: 'OpenAI / GPT',
    icon: Brain,
    color: 'bg-pink-100 text-pink-600',
    testEndpoint: 'test/ai',
    needsInput: true,
    inputLabel: 'Prompt de teste',
    inputPlaceholder: 'Olá, você está funcionando?',
    inputType: 'text',
  },
];

export function AdminApiHealthPage() {
  const [testInputs, setTestInputs] = useState<Record<string, string>>({});
  const [testResults, setTestResults] = useState<Record<string, object>>({});
  const [expandedService, setExpandedService] = useState<string | null>(null);

  const { data: healthData, isLoading, refetch: refetchHealth } = useQuery<HealthResponse>({
    queryKey: ['admin', 'api-health'],
    queryFn: () => httpClient.get<HealthResponse>('/admin/api-health/status'),
    refetchInterval: 30000,
  });

  const testEmailMutation = useMutation({
    mutationFn: (data: { to: string }) =>
      httpClient.post<EmailTestResponse>('/admin/api-health/test/email', data),
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, email: data }));
      setExpandedService('email');
    },
  });

  const testMemedMutation = useMutation({
    mutationFn: () => httpClient.post<TestResponse>('/admin/api-health/test/memed'),
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, memed: data }));
      setExpandedService('memed');
    },
  });

  const testPagBankMutation = useMutation({
    mutationFn: () => httpClient.post<TestResponse>('/admin/api-health/test/pagbank'),
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, pagbank: data }));
      setExpandedService('pagbank');
    },
  });

  const testDailyMutation = useMutation({
    mutationFn: () => httpClient.post<TestResponse>('/admin/api-health/test/daily'),
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, daily: data }));
      setExpandedService('daily');
    },
  });

  const testAiMutation = useMutation({
    mutationFn: (data: { prompt: string }) =>
      httpClient.post<AiTestResponse>('/admin/api-health/test/ai', data),
    onSuccess: (data) => {
      setTestResults((prev) => ({ ...prev, ai: data }));
      setExpandedService('ai');
    },
  });

  const runTest = (serviceName: string) => {
    switch (serviceName) {
      case 'email':
        testEmailMutation.mutate({ to: testInputs.email || 'admin@medikei.com.br' });
        break;
      case 'memed':
        testMemedMutation.mutate();
        break;
      case 'pagbank':
        testPagBankMutation.mutate();
        break;
      case 'daily':
        testDailyMutation.mutate();
        break;
      case 'ai':
        testAiMutation.mutate({ prompt: testInputs.ai || 'Olá, você está funcionando?' });
        break;
    }
  };

  const getServiceHealth = (serviceName: string): ServiceStatus | undefined => {
    return healthData?.services.find((s) => s.name === serviceName);
  };

  const isTesting = (serviceName: string): boolean => {
    switch (serviceName) {
      case 'email':
        return testEmailMutation.isPending;
      case 'memed':
        return testMemedMutation.isPending;
      case 'pagbank':
        return testPagBankMutation.isPending;
      case 'daily':
        return testDailyMutation.isPending;
      case 'ai':
        return testAiMutation.isPending;
      default:
        return false;
    }
  };

  const formatLatency = (ms?: number): string => {
    if (ms === undefined) return '-';
    return `${ms}ms`;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">API Health Dashboard</h1>
          <p className="text-sm text-gray-500">
            Status das integrações externas • Atualizado em{' '}
            {healthData?.timestamp
              ? new Date(healthData.timestamp).toLocaleString('pt-BR')
              : '-'}
          </p>
        </div>
        <button
          onClick={() => refetchHealth()}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {serviceConfig.map((service) => {
          const health = getServiceHealth(service.name);
          const testing = isTesting(service.name);
          const result = testResults[service.name];
          const Icon = service.icon;

          return (
            <Card key={service.name} className="relative">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-2 rounded-lg ${service.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      health?.healthy
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {health?.healthy ? (
                      <CheckCircle className="h-3 w-3" />
                    ) : (
                      <XCircle className="h-3 w-3" />
                    )}
                    {health?.healthy ? 'Online' : 'Offline'}
                  </div>
                </div>

                <h3 className="font-semibold text-gray-900">{service.label}</h3>
                <p className="text-xs text-gray-500 mb-2">{service.description}</p>

                {health && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                    <span>Latência: {formatLatency(health.latencyMs)}</span>
                    {health.provider && (
                      <>
                        <span>•</span>
                        <span>{health.provider}</span>
                      </>
                    )}
                  </div>
                )}

                {service.needsInput && (
                  <input
                    type={service.inputType}
                    placeholder={service.inputPlaceholder}
                    value={testInputs[service.name] || ''}
                    onChange={(e) =>
                      setTestInputs((prev) => ({ ...prev, [service.name]: e.target.value }))
                    }
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded mb-2"
                  />
                )}

                <button
                  onClick={() => runTest(service.name)}
                  disabled={testing}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  {testing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Testando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      Testar
                    </>
                  )}
                </button>

                {result && (
                  <button
                    onClick={() =>
                      setExpandedService(expandedService === service.name ? null : service.name)
                    }
                    className="w-full mt-2 text-xs text-blue-600 hover:text-blue-800"
                  >
                    {expandedService === service.name ? 'Ocultar resultado' : 'Ver resultado'}
                  </button>
                )}

                {expandedService === service.name && result && (
                  <div className="mt-3 p-2 bg-gray-50 rounded text-xs font-mono overflow-auto max-h-40">
                    <pre className="whitespace-pre-wrap">
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-blue-500" />
            Detalhes de Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Serviço</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Latência</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Provider</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Mensagem</th>
                  </tr>
                </thead>
                <tbody>
                  {healthData?.services.map((service) => (
                    <tr key={service.name} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3 font-medium capitalize">{service.name}</td>
                      <td className="py-2 px-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            service.healthy
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {service.healthy ? (
                            <CheckCircle className="h-3 w-3" />
                          ) : (
                            <XCircle className="h-3 w-3" />
                          )}
                          {service.healthy ? 'Healthy' : 'Unhealthy'}
                        </span>
                      </td>
                      <td className="py-2 px-3">{formatLatency(service.latencyMs)}</td>
                      <td className="py-2 px-3 text-gray-500">{service.provider || '-'}</td>
                      <td className="py-2 px-3 text-gray-500">{service.message || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
